import type { TutorBillingFlag } from './enums'
import type { TutorBillingRecordChip, TutorBillingTriStateOption } from './interfaces'
import type { TriState, TutorBillingFlagsPatch } from './types'

import { TutorBillingModal } from './enums'
import { useTutorBillingStore } from './store'
import { useTutorBillingBulkFlags } from '../api/bulk-flags.hook'
import { useTutorBillingListApi } from '../api/list.hook'
import { TUTOR_BILLING_BULK_FLAGS, TUTOR_BILLING_BULK_LIMIT, TUTOR_BILLING_FLAG_LABELS } from '../config/tutor-billing'
import { buildTutorBillingRecordChips } from '../lib/tutor-billing-fields'

const BULK_FLAG_OPTIONS: TutorBillingTriStateOption[] = [
  { text: 'Keep', value: null, tone: 'neutral' },
  { text: 'No', value: false, tone: 'no' },
  { text: 'Yes', value: true, tone: 'yes' },
]

const buildEmptyPatch = (): Record<TutorBillingFlag, TriState> =>
  Object.fromEntries(TUTOR_BILLING_BULK_FLAGS.map(flag => [flag, null])) as Record<TutorBillingFlag, TriState>

interface ModalTutorBillingBulkFlagsReturn {
  isOpen: ComputedRef<boolean>
  isSaving: ComputedRef<boolean>
  title: ComputedRef<string>
  applyText: ComputedRef<string>
  chips: ComputedRef<TutorBillingRecordChip[]>
  isOverLimit: ComputedRef<boolean>
  flags: TutorBillingFlag[]
  labels: Record<TutorBillingFlag, string>
  stateOptions: TutorBillingTriStateOption[]
  getValue: (flag: TutorBillingFlag) => TriState
  isValid: ComputedRef<boolean>
  setFlag: (flag: TutorBillingFlag, value: TriState) => void
  setClose: () => void
  setApply: () => Promise<void>
}

export const useModalTutorBillingBulkFlags = (): ModalTutorBillingBulkFlagsReturn => {
  const store = useTutorBillingStore()

  const patch = reactive<Record<TutorBillingFlag, TriState>>(buildEmptyPatch())

  const isOpen = computed<boolean>(() => store.isModal(TutorBillingModal.BulkFlags))
  const isSaving = computed<boolean>(() => store.isSaving)

  const selectedIds = computed<number[]>(() => store.getSelectedIds)
  const selectedCount = computed<number>(() => selectedIds.value.length)

  const title = computed<string>(() => `Edit ${selectedCount.value} selected records`)
  const applyText = computed<string>(() => `Apply to ${selectedCount.value} records`)

  const chips = computed<TutorBillingRecordChip[]>(() => buildTutorBillingRecordChips(selectedIds.value, store.getRows))

  const isOverLimit = computed<boolean>(() => selectedCount.value > TUTOR_BILLING_BULK_LIMIT)

  const getValue = (flag: TutorBillingFlag): TriState => patch[flag]

  const setFlag = (flag: TutorBillingFlag, value: TriState): void => {
    patch[flag] = value
  }

  const isValid = computed<boolean>(() =>
    selectedCount.value > 0
    && selectedCount.value <= TUTOR_BILLING_BULK_LIMIT
    && TUTOR_BILLING_BULK_FLAGS.some(flag => patch[flag] !== null))

  const setClose = (): void => {
    if (store.isSaving) return
    store.closeModal()
  }

  const setApply = async (): Promise<void> => {
    if (!isValid.value || store.isSaving) return

    const payload = Object.fromEntries(
      TUTOR_BILLING_BULK_FLAGS
        .filter(flag => patch[flag] !== null)
        .map(flag => [flag, patch[flag] as boolean]),
    ) as TutorBillingFlagsPatch

    const applied = await useTutorBillingBulkFlags(selectedIds.value, payload)

    if (!applied) return

    if (store.isModalFor(TutorBillingModal.BulkFlags, null)) store.closeModal()
    store.clearSelection()
    void useTutorBillingListApi()
  }

  watch(isOpen, (value) => {
    if (value) Object.assign(patch, buildEmptyPatch())
  })

  return {
    isOpen,
    isSaving,
    title,
    applyText,
    chips,
    isOverLimit,
    flags: TUTOR_BILLING_BULK_FLAGS,
    labels: TUTOR_BILLING_FLAG_LABELS,
    stateOptions: BULK_FLAG_OPTIONS,
    getValue,
    isValid,
    setFlag,
    setClose,
    setApply,
  }
}
